<template>
  <BaseDialog
    :is-visible="visible"
    class="pap-dialog"
    width="900px"
    :close-on-click-modal="true"
    :title="papText('postAssessmentPromotion.title')"
    @close-dialog="handleClose"
  >
    <template #body>
      <div
        class="plan-cards-grid"
        :class="{ 'is-single-banner': isSingleBanner }"
      >
        <div
          v-for="(banner, index) in sortedBanners"
          :key="index"
          class="plan-card"
          :class="{
            'is-discounted': banner.is_discounted,
            'is-single': isSingleBanner
          }"
        >
          <div class="plan-card-image-wrapper">
            <img
              v-show="imageLoaded[index]"
              :src="getImageSrc(banner)"
              :alt="banner.alt_text"
              class="plan-card-image"
              @load="handleImageLoad(index)"
            />
            <img
              v-show="!imageLoaded[index]"
              :src="placeholderImage"
              :alt="banner.alt_text"
              class="plan-card-placeholder"
            />
          </div>
          <button
            class="plan-card-cta"
            @click="handlePlanClick(banner)"
          >
            {{ papText('postAssessmentPromotion.ctaButton') }}
          </button>
        </div>
      </div>
      <p v-if="royalBenefitsNote" class="pap-royal-benefits-note">
        {{ royalBenefitsNote }}
      </p>
      <div class="pap-footer-note">
        <h4>{{ papText('postAssessmentPromotion.footerNote.title') }}</h4>
        <p>{{ papText('postAssessmentPromotion.footerNote.content') }}</p>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import { debounce } from '@/utils/common';
import { mapGetters } from 'vuex';
import logger from '@/modules/Logger';
import { getParentSiteUrl } from '@/utils/urlHelper';
import BaseDialog from '@/components/Base/Dialog';

export default {
  name: 'ModalPostAssessmentPromotion',
  components: { BaseDialog },
  props: {
    visible: { type: Boolean, required: true, default: false },
    banners: { type: Array, default: () => [] }
  },
  data() {
    return {
      imageLoaded: {},
      placeholderImage: null,
      isMobile: window.innerWidth < 960
    };
  },
  computed: {
    ...mapGetters(['isZipanService']),
    ...mapGetters('userOtherSetting', ['isTaiwanCountryId']),
    sortedBanners() {
      return [...this.banners].sort((a, b) => a.display_order - b.display_order);
    },
    isSingleBanner() {
      return this.banners.length === 1;
    },
    serviceKey() {
      return this.isZipanService ? 'zipan' : 'bizmates';
    },
    papLocale() {
      if (this.isZipanService) return 'en';
      if (this.isTaiwanCountryId) return 'zh';
      return this.$i18n.locale;
    },
    royalBenefitsNote() {
      if (this.isZipanService) return '';
      return this.papText('postAssessmentPromotion.royalBenefitsNote');
    }
  },
  mounted() {
    if (this.visible && this.banners.length > 0) {
      logger.info('[PAP] Promotion modal displayed', {
        operation: 'pap-modal:server-log',
        event: 'impression',
        banner_count: this.banners.length,
        lesson_id: this.$store.state.lessonId
      });
      this.generatePlaceholder();
    }
    this.debouncedPlaceholder = debounce(this.generatePlaceholder, 150);
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    papText(key) {
      const serviceKey = `${key}.${this.serviceKey}`;
      return this.$i18n.t(serviceKey, this.papLocale);
    },
    onResize() {
      const wasMobile = this.isMobile;
      const nowMobile = window.innerWidth < 960;
      if (wasMobile !== nowMobile) {
        this.isMobile = nowMobile;
        this.debouncedPlaceholder();

        // Only reset imageLoaded if at least one banner has different PC/SP images.
        // If all banners use the same URL for both, the browser won't re-fire @load
        // on the cached image, leaving them permanently hidden behind the placeholder.
        const hasDistinctImages = this.sortedBanners.some(
          (b) => b.image_url !== b.image_url_sp
        );
        if (hasDistinctImages) {
          this.imageLoaded = {};
        }
      }
    },
    generatePlaceholder() {
      const isDiscounted = this.sortedBanners[0]?.is_discounted || false;
      const isSingle = this.isSingleBanner && !this.isMobile;
      const width = isSingle ? 805 : 255;
      const height = isDiscounted ? 403 : 330;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, width, height);

      this.placeholderImage = canvas.toDataURL();
    },
    getImageSrc(banner) {
      return this.isMobile ? banner.image_url_sp : banner.image_url;
    },
    handleImageLoad(index) {
      this.$set(this.imageLoaded, index, true);
    },
    handlePlanClick(banner) {
      const promotionId = this.extractPromotionId(banner.payment_url);

      logger.info('[PAP] Plan CTA clicked', {
        operation: 'pap-modal:server-log',
        event: 'click',
        payment_url: banner.payment_url,
        promotion_id: promotionId,
        lesson_id: this.$store.state.lessonId
      });

      const target = banner.target || '_blank';
      const url = getParentSiteUrl(this.$store.state.countryId) + banner.payment_url;
      window.open(url, target);

      this.$emit('plan-clicked', banner);
    },
    handleClose() {
      logger.info('[PAP] Promotion modal dismissed', {
        operation: 'pap-modal:server-log',
        event: 'dismiss',
        lesson_id: this.$store.state.lessonId
      });
      this.$emit('close');
    },
    extractPromotionId(paymentUrl) {
      const match = paymentUrl.match(/plan_id=(\d+)/);
      return match ? match[1] : null;
    }
  }
};
</script>

<style lang="scss" scoped src="@/assets/scss/modal_post_assessment_promotion.scss"></style>
